import axios from 'axios';
import { jwtDecode, JwtPayload } from 'jwt-decode';

import { LoginCredentials, LoginResponse, User, SubscriptionType } from '@/types/auth';

// Extended JWT payload interface to include our custom fields
interface ExtendedJwtPayload extends JwtPayload {
  subscriptionEnd?: string;
  subscriptionType?: SubscriptionType;
  isSubscriptionActive?: boolean;
  canAddClients?: boolean;
}

interface ResetPasswordData {
  otp: string;
  password: string;
  confirm_password: string;
  token: string;
}

const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

export const authService = {
  async requestLoginOTP(email: string): Promise<void> {
    await axiosInstance.post('/api/proxy/auth/request-otp', { email: email.toLowerCase() });
  },

  async loginWithOTP(email: string, otp: string): Promise<LoginResponse> {
    const { data } = await axiosInstance.post('/api/proxy/auth/login-otp', {
      email: email.toLowerCase(),
      otp,
    });

    // Extract the token from the response and set it in the cookie
    await axiosInstance.post('/api/auth', { token: data.token });

    // Decode and return user info from token
    const decodedToken = jwtDecode<ExtendedJwtPayload>(data.token);
    const userInfo = {
      ...(jwtDecode(data.token) as User),
      subscriptionEnd: decodedToken.subscriptionEnd || null,
      subscriptionType: decodedToken.subscriptionType || null,
    };
    return { token: data.token, expiresIn: data.expiresIn, user: userInfo };
  },

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const { data } = await axiosInstance.post('/api/proxy/auth/login', credentials);

    // Extract the token from the response and set it in the cookie
    await axiosInstance.post('/api/auth', { token: data.access_token });

    // Decode and return user info from token
    const decodedToken = jwtDecode<ExtendedJwtPayload>(data.access_token);
    const userInfo = {
      ...(jwtDecode(data.access_token) as User),
      subscriptionEnd: decodedToken.subscriptionEnd || null,
      subscriptionType: decodedToken.subscriptionType || null,
    };
    return { token: data.access_token, expiresIn: data.expires_in, user: userInfo };
  },

  async logout() {
    await axiosInstance.delete('/api/auth');
  },

  async resetPassword(data: ResetPasswordData) {
    const response = await axiosInstance.post('/api/proxy/users/reset-password', data);
    return response.data;
  },

  async checkSession(): Promise<LoginResponse | null> {
    try {
      const { data } = await axiosInstance.get('/api/auth');
      if (data.token) {
        const decodedToken = jwtDecode<ExtendedJwtPayload>(data.token);
        const userInfo = {
          ...(jwtDecode(data.token) as User),
          subscriptionEnd: decodedToken.subscriptionEnd || null,
          subscriptionType: decodedToken.subscriptionType || null,
        };
        console.log(userInfo);
        return {
          token: data.token,
          expiresIn: data.expiresIn,
          user: userInfo,
        };
      }
      return null;
    } catch (error: any) {
      // Don't call logout for 401 errors - they're normal for users without sessions
      if (error.response?.status !== 401) {
        await this.logout();
      }
      return null;
    }
  },

  async checkEmail(email: string): Promise<{ exists: boolean }> {
    const { data } = await axiosInstance.get(
      `/api/proxy/auth/check-email?email=${encodeURIComponent(email.toLowerCase())}`,
    );
    return data;
  },

  async getGoogleAuthUrl(): Promise<{ url: string }> {
    const { data } = await axiosInstance.get('/api/proxy/auth/google/url');
    return data;
  },

  async handleGoogleCallback(code: string): Promise<{ user: User; session: string }> {
    const { data } = await axiosInstance.get(
      `/api/proxy/auth/google/callback?code=${encodeURIComponent(code)}`,
    );

    // Store the token in the auth cookie
    await axiosInstance.post('/api/auth', { token: data.token });

    return {
      user: data.user,
      session: data.token,
    };
  },

  async signup(formData: {
    firstName: string;
    lastName: string;
    email: string;
  }): Promise<{ message: string }> {
    const { data } = await axiosInstance.post('/api/proxy/auth/signup', formData);
    return data;
  },

  async verifyOTP(email: string, otp: string): Promise<LoginResponse> {
    const { data } = await axiosInstance.post(
      '/api/proxy/auth/verify-otp',
      { email, otp },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    // Extract the token from the response and set it in the cookie
    await axiosInstance.post('/api/auth', { token: data.token });

    // Return the response with decoded user info and ensure subscription data is included
    const user = data.user;
    if (data.token) {
      // If we have a token, decode it to get subscription info
      const decodedToken = jwtDecode<ExtendedJwtPayload>(data.token);
      if (decodedToken && typeof decodedToken === 'object') {
        // Add subscription data to user object if available in token
        user.subscriptionEnd = decodedToken.subscriptionEnd || user.subscriptionEnd || null;
        user.subscriptionType = decodedToken.subscriptionType || user.subscriptionType || null;
      }
    }

    return {
      token: data.token,
      user: user,
      expiresIn: 3600, // 1 hour
    };
  },

  async saveSession(token: string) {
    await axiosInstance.post('/api/auth', { token });
  },

  /**
   * Refresh token to get updated subscription info
   * This is used after subscription changes to ensure token contains latest data
   */
  async refreshToken(): Promise<LoginResponse> {
    const { data } = await axiosInstance.post('/api/proxy/auth/refresh-token');

    // Save the new token to update the cookie
    await this.saveSession(data.token);

    // Decode and return user info from token
    const decodedToken = jwtDecode<ExtendedJwtPayload>(data.token);
    const userInfo = {
      ...(jwtDecode(data.token) as User),
      subscriptionEnd: decodedToken.subscriptionEnd || null,
      subscriptionType: decodedToken.subscriptionType || null,
      isSubscriptionActive: decodedToken.isSubscriptionActive || false,
    };

    return {
      token: data.token,
      user: userInfo,
      expiresIn: 3600, // 1 hour
    };
  },
};
